import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import _ from "lodash"
import type { RootState } from "../store"

interface TestResultsDraft {
	eventId: string
	profileId: string
	taskId: string
	results?: Record<string, unknown>
	[key: string]: unknown
}

interface PlayerMatchPerformanceRatingDraft {
	eventId: string
	profileId: string
	[key: string]: unknown
}

interface DraftState {
	testResults: TestResultsDraft[]
	playerMatchPerformanceRatings: PlayerMatchPerformanceRatingDraft[]
	// Keyed by rootGroupId; each entry is a caller-shaped list of invites
	inviteMembersToRootGroup: Record<string, any>
}

const initialState: DraftState = {
	testResults: [],
	playerMatchPerformanceRatings: [],
	inviteMembersToRootGroup: {},
}

export const draftSlice = createSlice({
	name: "draft",
	initialState,
	reducers: {
		addTestResultsDraft(state, { payload }: PayloadAction<TestResultsDraft>) {
			const index = state.testResults.findIndex((item) => {
				return item.eventId === payload.eventId && item.profileId === payload.profileId && item.taskId === payload.taskId
			})
			if (index !== -1) state.testResults[index] = payload
			else state.testResults.push(payload)
		},
		removeTestResultsDraftForProfile(
			state,
			{ payload }: PayloadAction<{ eventId: string; profileId: string; targetProfile: string; taskId: string }>
		) {
			const { eventId, profileId, targetProfile, taskId } = payload
			const obj = state.testResults.find((item) => {
				return item.eventId === eventId && item.profileId === profileId && item.taskId === taskId
			})
			if (obj?.results) {
				// Structure can be either `results[testId][profileId]` or `results[profileId]`
				Object.keys(obj.results).forEach((key) => {
					const item = obj.results![key]
					if (_.isObject(item)) {
						Object.keys(item).forEach((key2) => {
							if (key2 === targetProfile) {
								delete (item as Record<string, unknown>)[key2]
							}
						})
					} else {
						if (key === targetProfile) {
							delete obj.results![key]
						}
					}
				})
			}
		},
		addPlayerMatchPerformanceRatingsDraft(state, { payload }: PayloadAction<PlayerMatchPerformanceRatingDraft>) {
			const index = state.playerMatchPerformanceRatings.findIndex((item) => {
				return item.eventId === payload.eventId && item.profileId === payload.profileId
			})
			if (index !== -1) state.playerMatchPerformanceRatings[index] = payload
			else state.playerMatchPerformanceRatings.push(payload)
		},
		removePlayerMatchPerformanceRatingsDraft(state, { payload }: PayloadAction<{ eventId: string; profileId: string }>) {
			state.playerMatchPerformanceRatings = state.playerMatchPerformanceRatings.filter(
				(rating) => rating.eventId !== payload.eventId && rating.profileId !== payload.profileId
			)
		},
		addInviteMembersToRootGroup(state, { payload }: PayloadAction<{ rootGroupId: string; invites: unknown }>) {
			state.inviteMembersToRootGroup[payload.rootGroupId] = payload.invites
		},
		removeInviteMembersToRootGroup(state, { payload }: PayloadAction<string>) {
			delete state.inviteMembersToRootGroup[payload]
		},
	},
})

export const {
	addTestResultsDraft,
	removeTestResultsDraftForProfile,
	addPlayerMatchPerformanceRatingsDraft,
	removePlayerMatchPerformanceRatingsDraft,
	addInviteMembersToRootGroup,
	removeInviteMembersToRootGroup,
} = draftSlice.actions
export const draftSelector = (state: RootState) => state.draft
